import { InformationCircle } from "@ignite-analytics/icons";
import { Alert, Box, Grid, Link, Skeleton, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useEffect, useMemo } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useParams, useSearchParams } from "react-router-dom";

import AssessmentForm from "../../components/Form";
import { useSubmittedDialogs } from "../../components/Form/SubmittedDialogs";
import Layout from "../../components/Layout";
import MessagePage from "../../components/MessagePage";
import QuestionnaireHeader from "../../components/Questionnaire/Header";
import { QuestionnaireTopBar, SaveIndicator } from "../../components/QuestionnaireTopBar";
import SectionsNav from "../../components/SectionsNav";
import { AssessmentType } from "../../globals/types";
import { useAnswerFilesV2 } from "../../hooks/useAnswerFilesV2";
import { useAssessmentDetail, useRefetchAssessment } from "../../hooks/useAssessmentDetail";
import { useAssessmentNavigation } from "../../hooks/useAssessmentNavigation";
import { useCodeOfConduct } from "../../hooks/useCodeOfConduct";
import { useSaveAnswersV2 } from "../../hooks/useSaveAnswersV2";
import useSectionIndex from "../../hooks/useSectionIndex";
import { useSupportingDocuments } from "../../hooks/useSupportingDocuments";
import useToken from "../../hooks/useToken";
import { useAlert } from "../../lib/Alerts";
import { authHeader, FormsApiError, formsFetch, isNotFound, unwrap } from "../../lib/formsApi";
import { track } from "../../lib/track";
import { fromAnswersV2, withPrefill } from "../../questionnaire/answers";
import { CodeOfConductContext } from "../../questionnaire/codeOfConduct";
import type { QuestionnaireRevisionContents } from "../../questionnaire/contents";
import { FileActionsContext } from "../../questionnaire/fileActions";
import { Questionnaire } from "../../questionnaire/Questionnaire";

import SharingConsentToggle from "./SharingConsentToggle";

const cardSx = {
    backgroundColor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 1px 2px rgba(13, 16, 23, 0.04)",
    overflow: "hidden",
} as const;

const QuestionnairePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get("preview") === "true";
    const { id } = useParams<{ id: string }>();
    const { data: assessment, isPending, isPlaceholderData, error, refetch } = useAssessmentDetail(id ?? "");
    const refetchAssessment = useRefetchAssessment();
    const { backUrl } = useAssessmentNavigation();
    const { token, decoded, viewOnly } = useToken();

    const questionnaire = assessment?.questionnaire;
    const contents = assessment?.revision?.contents as QuestionnaireRevisionContents | undefined;
    const [sectionIndex] = useSectionIndex();
    const answersEnabled = !preview && !viewOnly && !!assessment?.editable;
    const {
        save: saveAnswer,
        answerId,
        flush,
        status: saveStatus,
    } = useSaveAnswersV2(assessment?.id ?? "", token, assessment?.answersV2 ?? [], answersEnabled);
    const fileActions = useAnswerFilesV2(assessment?.id ?? "", decoded.tenant, token, answerId);
    const prefill = useSupportingDocuments(assessment?.id ?? "", token, answersEnabled && !!contents);
    const codeOfConduct = useCodeOfConduct(token, assessment?.supplierName ?? "", decoded.customerName ?? "", {
        digitalSigning: !!assessment?.receiverEmail,
        signingAs: assessment?.receiverEmail ?? undefined,
        signerName: assessment?.receiverName ?? undefined,
    });
    const { show: showSubmitted, dialogs: submittedDialogs } = useSubmittedDialogs(
        assessment?.type ?? AssessmentType.External
    );
    const { alertUser } = useAlert();
    const { formatMessage } = useIntl();

    const questionCount = useMemo(
        () => questionnaire?.sections.reduce((sum, curr) => sum + curr.questions.length, 0),
        [questionnaire]
    );

    const hasNoAnswers = assessment?.answers.length === 0 && assessment?.answersV2.length === 0;
    const hasAnswerableForm = contents
        ? contents.questions.length > 0
        : !!questionCount && questionnaire?.sections[sectionIndex] !== undefined;
    useEffect(() => {
        if (preview || viewOnly || !assessment?.id || !hasNoAnswers || !hasAnswerableForm) return;
        const assessmentId = assessment.id;
        const start = async () => {
            unwrap(
                await formsFetch.POST("/api/ast/{id}/start", {
                    params: { path: { id: assessmentId } },
                    headers: authHeader(token),
                }),
                "start assessment"
            );
            await refetchAssessment();
        };
        const onInteraction = () => {
            window.removeEventListener("pointerdown", onInteraction);
            window.removeEventListener("keydown", onInteraction);
            start().catch((err) => Sentry.captureException(err));
        };
        window.addEventListener("pointerdown", onInteraction);
        window.addEventListener("keydown", onInteraction);
        return () => {
            window.removeEventListener("pointerdown", onInteraction);
            window.removeEventListener("keydown", onInteraction);
        };
    }, [preview, viewOnly, assessment?.id, hasNoAnswers, hasAnswerableForm, token, refetchAssessment]);

    if (isPending) {
        return (
            <Layout appBar>
                <Stack spacing={3} maxWidth="100%" mx="auto" mt={6} p={3}>
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="95%" height={16} />
                    {[1, 2, 3].map((q) => (
                        <Skeleton key={q} variant="rectangular" height={180} sx={{ borderRadius: 2, mt: 3 }} />
                    ))}
                </Stack>
            </Layout>
        );
    }

    if (viewOnly && error instanceof FormsApiError && error.status === 401) {
        return <MessagePage reason="viewExpired" />;
    }

    // The assessment behind this link no longer exists (deleted/expired), not a load failure.
    if (isNotFound(error)) {
        return <MessagePage reason="404" />;
    }

    if (contents ? contents.questions.length === 0 : questionCount === 0) {
        return <MessagePage reason="missingQuestionnaire" />;
    }

    // If request errored, or we fail to get questionnaire section, show error page.
    if (!assessment || !questionnaire || (!contents && questionnaire.sections[sectionIndex] === undefined)) {
        Sentry.captureException("no questionnaire data");
        return <MessagePage reason="other" />;
    }

    const sideMenu = questionnaire?.sections?.length > 1;

    const showConsentToggle = assessment?.type === AssessmentType.External && sectionIndex === 0;

    const answeredQuestionIds = new Set(
        assessment.answers
            .filter((a) => a.value.some((v) => v && v.trim() !== "") || a.fileNames.length > 0)
            .map((a) => a.questionId)
    );
    const sectionProgress = questionnaire.sections.map((s) => ({
        answered: s.questions.filter((q) => answeredQuestionIds.has(q.id)).length,
        total: s.questions.length,
    }));

    const tenantName = decoded.customerName ?? "";
    const requesterName = decoded.requesterName ?? "";
    const requesterEmail = decoded.requesterEmail ?? "";
    const campaign = assessment.campaign;
    const showIntro =
        sectionIndex === 0 &&
        !decoded.receiverEmail &&
        (!!tenantName || !!requesterName || !!campaign?.description || !!campaign?.dueDate);
    const intro = showIntro ? (
        <Stack
            spacing={1}
            sx={{ mb: 3, pb: 3, borderBottom: "1px solid", borderColor: "divider" }}
            data-testid="questionnaire-intro"
        >
            {tenantName && (
                <Typography variant="textSm" color="textTextSecondary">
                    <FormattedMessage
                        defaultMessage="{tenantName} has requested the following information from {supplierName}. Your answers help them assess and work with your company."
                        values={{ tenantName, supplierName: assessment.supplierName }}
                    />
                </Typography>
            )}
            {requesterName && (
                <Typography variant="textSm" color="textTextSecondary">
                    <FormattedMessage defaultMessage="Requested by {requesterName}" values={{ requesterName }} />
                    {requesterEmail && (
                        <>
                            {" ("}
                            <Link href={`mailto:${requesterEmail}`}>{requesterEmail}</Link>
                            {")"}
                        </>
                    )}
                </Typography>
            )}
            {campaign?.description && (
                <Typography variant="textSm" color="textTextSecondary" sx={{ whiteSpace: "pre-line" }}>
                    {campaign.description}
                </Typography>
            )}
            {campaign?.dueDate && (
                <Typography variant="textSm" color="textTextHelper">
                    <FormattedMessage
                        defaultMessage="Please respond by {date}."
                        values={{
                            date: (
                                <FormattedDate
                                    value={new Date(campaign.dueDate)}
                                    day="numeric"
                                    month="long"
                                    year="numeric"
                                />
                            ),
                        }}
                    />
                </Typography>
            )}
        </Stack>
    ) : null;

    const viewNotice = viewOnly && (
        <Alert severity="info" sx={{ mb: 3 }}>
            <FormattedMessage defaultMessage="You are viewing this submission. It cannot be changed here." />
        </Alert>
    );

    const infoFooter = viewOnly ? null : assessment.editable ? (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1} sx={{ mt: 2, px: 2 }}>
            <InformationCircle sx={{ color: (theme) => theme.palette.text.textHelper }} />
            <Typography variant="textSm" color="textTextHelper">
                {assessment.submittedAt ? (
                    <FormattedMessage defaultMessage="Your feedback is submitted. Changes are saved automatically until the evaluation concludes." />
                ) : (
                    <FormattedMessage defaultMessage="Questions will be saved automatically when you make changes. Your answer will be sent once you click the submit button." />
                )}
            </Typography>
        </Stack>
    ) : assessment.expired ? (
        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1} sx={{ mt: 2, px: 2 }}>
            <InformationCircle sx={{ color: (theme) => theme.palette.text.textHelper }} />
            <Typography variant="textSm" color="textTextHelper">
                <FormattedMessage defaultMessage="This evaluation has been closed. Your answers can no longer be changed." />
            </Typography>
        </Stack>
    ) : null;

    const header = (
        <QuestionnaireHeader
            questionnaire={questionnaire}
            supplierName={assessment.supplierName}
            url={viewOnly ? undefined : backUrl}
            evaluationScope={assessment.evaluationScope}
            hideLanguageSwitcher={!!contents}
        />
    );

    const submitV2 = async () => {
        if (preview) return;
        track("submitted form", { type: assessment.type, sharingConsent: assessment.dataSharingConsent });
        if (!(await flush())) return;
        try {
            const result = unwrap(
                await formsFetch.POST("/api/ast/{id}/submit", {
                    params: { path: { id: assessment.id } },
                    headers: authHeader(token),
                }),
                "submit assessment"
            );
            if (!result.ok) {
                const missing = (result.missingResponse ?? []).map((question) => question.id).join(",");
                throw new Error(`submit was refused, missing answers: ${missing}`);
            }
        } catch (err) {
            if (!(err instanceof FormsApiError && err.status === 409)) {
                Sentry.captureException(err);
                alertUser({
                    value: formatMessage({ defaultMessage: "Your answers could not be submitted. Please try again." }),
                    severity: "error",
                });
                return;
            }
        }
        await refetchAssessment().catch((err) => Sentry.captureException(err));
        showSubmitted();
    };

    if (contents) {
        const submittedNotice = assessment.submittedAt && (
            <Alert severity="success" sx={{ mb: intro || showConsentToggle ? 3 : 0 }}>
                <FormattedMessage
                    defaultMessage="Submitted on {date}."
                    values={{
                        date: (
                            <FormattedDate
                                value={new Date(assessment.submittedAt)}
                                day="numeric"
                                month="long"
                                year="numeric"
                            />
                        ),
                    }}
                />
            </Alert>
        );
        return (
            <Layout
                topBar={
                    <QuestionnaireTopBar
                        translating={isPlaceholderData}
                        start={answersEnabled && <SaveIndicator status={saveStatus} />}
                    />
                }
            >
                <Box sx={{ my: 3 }}>
                    <FileActionsContext.Provider value={preview ? undefined : fileActions}>
                        <CodeOfConductContext.Provider value={codeOfConduct}>
                            <Questionnaire
                                contents={contents}
                                documentTypes={assessment?.revision?.documentTypes}
                                numbers={assessment?.revision?.numbers}
                                header={header}
                                initialAnswers={withPrefill(contents.questions, fromAnswersV2(assessment.answersV2))}
                                onChange={saveAnswer}
                                onSubmit={assessment.submittedAt || viewOnly ? undefined : submitV2}
                                readOnly={!answersEnabled}
                                prefill={prefill}
                                intro={
                                    (viewNotice || submittedNotice || intro || showConsentToggle) && (
                                        <Box>
                                            {viewNotice}
                                            {submittedNotice}
                                            {intro}
                                            {showConsentToggle && (
                                                <SharingConsentToggle
                                                    assessment={assessment}
                                                    disabled={viewOnly || !assessment.editable}
                                                />
                                            )}
                                        </Box>
                                    )
                                }
                            />
                        </CodeOfConductContext.Provider>
                    </FileActionsContext.Provider>
                    {infoFooter}
                </Box>
                {submittedDialogs}
            </Layout>
        );
    }

    const formCard = (
        <Box sx={cardSx}>
            {header}
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
                {viewNotice}
                {intro}
                {showConsentToggle && (
                    <SharingConsentToggle assessment={assessment} disabled={viewOnly || !assessment.editable} />
                )}
                <AssessmentForm
                    assessment={assessment}
                    questionnaire={questionnaire}
                    preview={preview || viewOnly}
                    readOnly={viewOnly}
                    refetch={refetch}
                />
            </Box>
        </Box>
    );

    if (!sideMenu) {
        return (
            <Layout>
                <Box sx={{ maxWidth: 760, mx: "auto", my: 3 }}>
                    {formCard}
                    {infoFooter}
                </Box>
            </Layout>
        );
    }

    return (
        <Layout>
            <Grid container columnSpacing={2} rowSpacing={2} sx={{ my: 3 }}>
                <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <Box sx={{ position: { md: "sticky" }, top: 24 }}>
                        <SectionsNav questionnaire={questionnaire} progress={sectionProgress} />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                    {formCard}
                    {infoFooter}
                </Grid>
            </Grid>
        </Layout>
    );
};

export default QuestionnairePage;
